
import * as React from "react"
import { cva } from "class-variance-authority"
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible"
import { cn } from "@/lib/utils"

const SidebarContext = React.createContext<{
  isCollapsed: boolean
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>
}>({
  isCollapsed: false,
  setIsCollapsed: () => {},
})

export function SidebarProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [isCollapsed, setIsCollapsed] = React.useState(false)

  return (
    <SidebarContext.Provider value={{ isCollapsed, setIsCollapsed }}>
      {children}
    </SidebarContext.Provider>
  )
}

export function useSidebar() {
  const context = React.useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider")
  }
  return context
}

export function Sidebar({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const { isCollapsed } = useSidebar()

  return (
    <aside
      className={cn(
        "fixed top-0 left-0 z-20 h-full w-64 border-r bg-background transition-all duration-300 ease-in-out",
        isCollapsed && "w-16",
        className
      )}
      {...props}
    >
      <Collapsible open={!isCollapsed} className="h-full">
        {children}
      </Collapsible>
    </aside>
  )
}

export function SidebarHeader({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const { isCollapsed } = useSidebar()

  return (
    <div
      className={cn(
        "flex h-14 items-center border-b px-4",
        isCollapsed && "justify-center px-0",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export function SidebarContent({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex flex-col gap-2 p-4", className)}
      {...props}
    >
      {children}
    </div>
  )
}

export function SidebarGroup({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex flex-col gap-1", className)}
      {...props}
    >
      {children}
    </div>
  )
}

export function SidebarGroupLabel({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const { isCollapsed } = useSidebar()

  if (isCollapsed) {
    return null
  }

  return (
    <div
      className={cn("px-2 text-xs font-semibold text-muted-foreground", className)}
      {...props}
    >
      {children}
    </div>
  )
}

export function SidebarGroupContent({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex flex-col gap-1", className)}
      {...props}
    >
      {children}
    </div>
  )
}

export function SidebarFooter({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const { isCollapsed } = useSidebar()

  return (
    <div
      className={cn(
        "absolute bottom-0 flex w-full border-t p-4",
        isCollapsed && "justify-center px-0",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export function SidebarTrigger({
  className,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { isCollapsed, setIsCollapsed } = useSidebar()

  return (
    <button
      type="button"
      className={cn(
        "inline-flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
        className
      )}
      onClick={() => setIsCollapsed(!isCollapsed)}
      {...props}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={cn(
          "h-6 w-6 transition-transform",
          isCollapsed ? "rotate-180" : "rotate-0"
        )}
      >
        <path d="M18 6L6 18M6 6l12 12" />
      </svg>
      <span className="sr-only">Toggle navigation</span>
    </button>
  )
}

export function SidebarMenu({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex flex-col gap-1", className)}
      {...props}
    >
      {children}
    </div>
  )
}

export function SidebarMenuItem({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex items-center", className)}
      {...props}
    >
      {children}
    </div>
  )
}

const sidebarMenuButtonVariants = cva(
  "inline-flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "hover:bg-muted hover:text-foreground",
        active: "bg-muted text-foreground"
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

interface SidebarMenuButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "active"
  asChild?: boolean
}

export function SidebarMenuButton({
  className,
  variant,
  asChild,
  ...props
}: SidebarMenuButtonProps) {
  const { isCollapsed } = useSidebar()
  let Component: any = "button"
  let compoundProps = props

  if (asChild) {
    Component = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(({ className, children, ...props }, ref) => {
      const child = React.Children.only(children as React.ReactElement)
      return React.cloneElement(child, {
        ref,
        className: cn(className, child.props.className),
        ...props,
      })
    })
    Component.displayName = "SidebarMenuButtonAsChild"
  }

  return (
    <Component
      className={cn(
        sidebarMenuButtonVariants({ variant }),
        isCollapsed && "justify-center px-0 w-9",
        className
      )}
      {...compoundProps}
    >
      {asChild ? props.children : (
        isCollapsed ? (
          <>
            {React.Children.map(props.children, (child) => {
              if (React.isValidElement(child) && typeof child.type !== 'string') {
                return child
              }
              return null
            })}
          </>
        ) : (
          props.children
        )
      )}
    </Component>
  )
}

export function SidebarSection({
  className,
  title,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  title: string
}) {
  const { isCollapsed } = useSidebar()

  return (
    <div className={cn("flex flex-col gap-2", className)} {...props}>
      {!isCollapsed && (
        <h3 className="px-2 text-xs font-semibold text-muted-foreground">{title}</h3>
      )}
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  )
}

export function CollapsibleSidebarMenu({
  title,
  icon,
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  title: string
  icon?: React.ReactNode
}) {
  const { isCollapsed } = useSidebar()
  const [isOpen, setIsOpen] = React.useState(false)

  return (
    <Collapsible
      open={isCollapsed ? false : isOpen}
      onOpenChange={isCollapsed ? () => {} : setIsOpen}
      className={cn("flex flex-col", className)}
      {...props}
    >
      <div
        onClick={() => !isCollapsed && setIsOpen(!isOpen)}
        className={cn(
          "flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5 text-sm font-medium hover:bg-muted",
          isOpen && !isCollapsed && "bg-muted"
        )}
      >
        <div className="flex items-center gap-2">
          {icon}
          {!isCollapsed && <span>{title}</span>}
        </div>
        {!isCollapsed && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={cn(
              "h-4 w-4 transition-transform",
              isOpen ? "rotate-180" : "rotate-0"
            )}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        )}
      </div>
      <CollapsibleContent className="pl-4">
        <div className="flex flex-col gap-1 pt-1">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

export function SidebarExpandToggle() {
  const { isCollapsed, setIsCollapsed } = useSidebar()
  
  return (
    <button 
      onClick={() => setIsCollapsed(!isCollapsed)}
      className="w-full flex items-center justify-center py-2 hover:bg-muted rounded-md"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={cn(
          "h-5 w-5 transition-transform",
          isCollapsed ? "rotate-0" : "rotate-180"
        )}
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
      {!isCollapsed && <span className="ml-2 text-xs">Collapse</span>}
    </button>
  )
}
